import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  Home, Cable, Users2, CreditCard, Settings as SettingsIcon, LogOut, Menu, X, ChevronDown,
  ChevronRight, RefreshCw, AlertTriangle, PlugZap, Check, BookOpenCheck, Package, ShoppingCart,
  ShoppingBag, UserCog, BarChart3,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { TallyProvider, syncState, useTally } from "@/context/TallyContext";
import { api, apiError } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { toast } from "sonner";
import AnnouncementBanner from "@/components/AnnouncementBanner";

/**
 * SyncBooks layout: navy top bar, light sidebar, white work area — the
 * Zoho Books arrangement, in our own colours and name.
 *
 * NAV entries are either links or groups of links. A group opens when one of
 * its pages is active. `module` gates an entry on the organisation's plan.
 */
const NAV = [
  { to: "/home", label: "Home", icon: Home },
  { to: "/items", label: "Items", icon: Package },
  { label: "Sales", icon: ShoppingCart, children: [
    { to: "/customers", label: "Customers" },
    { to: "/invoices", label: "Invoices" },
    { to: "/receipts", label: "Payments Received" },
    { to: "/credit-notes", label: "Credit Notes" },
  ] },
  { label: "Purchases", icon: ShoppingBag, children: [
    { to: "/vendors", label: "Vendors" },
    { to: "/bills", label: "Bills" },
    { to: "/payments", label: "Payments Made" },
    { to: "/debit-notes", label: "Vendor Credits" },
  ] },
  { label: "Accountant", icon: UserCog, children: [
    { to: "/accounts", label: "Chart of Accounts" },
    { to: "/journals", label: "Journals" },
  ] },
  { to: "/reports", label: "Reports", icon: BarChart3 },
];

const FOOTER_NAV = [
  { to: "/connect", label: "Tally connection", icon: Cable },
  { to: "/team", label: "Team", icon: Users2, module: "multi_user" },
  { to: "/billing", label: "Subscription", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

const ROLE_LABEL = { OWNER: "Owner", STAFF: "Staff", VIEWER: "CA / Viewer" };

function visible(entries, modules) {
  return entries.filter((n) => !n.module || (modules || []).includes(n.module));
}

function NavItem({ item, onNavigate }) {
  const { pathname } = useLocation();
  const Icon = item.icon;
  const groupActive = item.children?.some((c) => pathname.startsWith(c.to));
  const [open, setOpen] = useState(groupActive);
  useEffect(() => {
    if (groupActive) setOpen(true);
  }, [groupActive]);

  if (item.children) {
    return (
      <div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-[13.5px] text-slate-700 hover:bg-slate-200/70"
        >
          {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
          <Icon size={16} strokeWidth={1.8} />
          {item.label}
        </button>
        {open && (
          <div className="mt-0.5 space-y-0.5 pl-8">
            {item.children.map((c) => (
              <NavLink key={c.to} to={c.to} onClick={onNavigate}
                className={({ isActive }) => `block rounded-md px-3 py-1.5 text-[13px] ${
                  isActive ? "bg-[#408dfb] font-medium text-white" : "text-slate-600 hover:bg-slate-200/70"}`}>
                {c.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <NavLink to={item.to} onClick={onNavigate} data-testid={`nav-${item.to.slice(1)}`} end={item.to === "/home"}
      className={({ isActive }) => `flex items-center gap-2.5 rounded-md px-3 py-2 text-[13.5px] ${
        isActive ? "bg-[#408dfb] font-medium text-white" : "text-slate-700 hover:bg-slate-200/70"}`}>
      <Icon size={16} strokeWidth={1.8} />
      {item.label}
    </NavLink>
  );
}

/** The sync status in the top bar: where the Tally data stands, at a glance. */
function SyncPill() {
  const { company, loading } = useTally();
  if (loading) return null;
  const state = syncState(company);
  const base = "flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap";
  if (state === "NOT_CONNECTED") {
    return (
      <Link to="/connect" className={`${base} bg-[#408dfb] text-white hover:bg-[#2f7bea]`} data-testid="sync-pill">
        <PlugZap size={13} /> Connect Tally
      </Link>
    );
  }
  if (state === "AGENT_OFFLINE" || state === "COMPANY_CLOSED") {
    return (
      <Link to="/connect" className={`${base} bg-amber-400/15 text-amber-300`} data-testid="sync-pill">
        <AlertTriangle size={13} />
        {state === "AGENT_OFFLINE" ? "Tally agent offline" : "Company closed in Tally"}
      </Link>
    );
  }
  if (state === "LOADING") {
    const load = company.initial_load || {};
    return (
      <Link to="/connect" className={`${base} bg-white/10 text-slate-100`} data-testid="sync-pill">
        <RefreshCw size={13} className="animate-spin" />
        Loading from Tally {load.months_total ? `${load.months_done}/${load.months_total} months` : "…"}
      </Link>
    );
  }
  return (
    <Link to="/connect" className={`${base} bg-emerald-400/15 text-emerald-300`} data-testid="sync-pill"
      title={`Last change received ${timeAgo(company.last_batch_at)}`}>
      <Check size={13} /> Synced {timeAgo(company.last_scan_at || company.last_batch_at)}
    </Link>
  );
}

function OrgMenu() {
  const { organization, organizations } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  // A switch changes which tenant every request resolves to: reload rather
  // than try to invalidate every cached view in place.
  const switchOrg = async (organization_id) => {
    try {
      await api.post("/auth/switch-organization", { organization_id });
      window.location.assign("/home");
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const many = (organizations || []).length > 1;
  // Once a company is connected, people think of the tenant by its name in
  // Tally, not by the organisation name they typed at signup. Display only:
  // Settings still edits, and shows, the organisation's own name.
  const label = (o) => o?.tally_name || o?.name;
  const current = (organizations || []).find((o) => o.is_current);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => many && setOpen((v) => !v)} data-testid="org-switcher"
        className={`flex items-center gap-1 rounded px-2 py-1 text-sm text-white ${many ? "hover:bg-white/10" : "cursor-default"}`}>
        <span className="max-w-[180px] truncate">{label(current) || organization?.name}</span>
        {many && <ChevronDown size={14} className="text-slate-300" />}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {organizations.map((o) => (
            <button key={o.id} disabled={o.is_current} onClick={() => switchOrg(o.id)}
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                o.is_current ? "bg-blue-50 font-medium text-blue-700" : "text-slate-700 hover:bg-slate-50"}`}>
              <span className="truncate">{label(o)}</span>
              {o.is_current && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { user, role, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const initial = (user?.name || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} data-testid="user-menu"
        className="grid h-8 w-8 place-items-center rounded-full bg-[#5b5fc7] text-sm font-semibold text-white ring-2 ring-white/20">
        {initial}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-56 rounded-md border border-slate-200 bg-white p-3 shadow-lg">
          <div className="truncate text-sm font-medium text-slate-900">{user?.name}</div>
          <div className="truncate text-xs text-slate-500">{user?.email}</div>
          <div className="mt-1 text-xs text-blue-600">{ROLE_LABEL[role] || role}</div>
          <button onClick={logout} data-testid="logout-btn"
            className="mt-3 flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-rose-600 hover:bg-rose-50">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function Shell({ children }) {
  const { modules } = useAuth();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center gap-3 bg-[#1f2433] px-3 sm:px-4 print:hidden">
        <button className="text-slate-300 lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open">
          <Menu size={20} />
        </button>
        <Link to="/home" className="flex shrink-0 items-center gap-2 whitespace-nowrap text-white lg:w-[216px]">
          <BookOpenCheck size={22} className="text-[#61a4ff]" />
          <span className="text-[17px] font-semibold tracking-tight">SyncBooks</span>
        </Link>
        <div className="flex-1" />
        <SyncPill />
        <div className="hidden h-6 w-px bg-white/15 sm:block" />
        {/* On a phone the organisation's name is on the page itself; the bar keeps sync and account. */}
        <div className="hidden sm:block"><OrgMenu /></div>
        <Link to="/settings" className="hidden rounded p-1.5 text-slate-300 hover:bg-white/10 hover:text-white sm:block" title="Settings">
          <SettingsIcon size={18} />
        </Link>
        <UserMenu />
      </header>

      <aside data-testid="app-sidebar"
        className={`fixed bottom-0 left-0 top-14 z-30 flex w-[232px] flex-col border-r border-slate-200 bg-[#f7f8fa] transition-transform duration-200 lg:translate-x-0 print:hidden ${
          open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex justify-end p-2 lg:hidden">
          <button className="text-slate-500" onClick={close} data-testid="sidebar-close"><X size={18} /></button>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
          {visible(NAV, modules).map((item) => <NavItem key={item.to || item.label} item={item} onNavigate={close} />)}
        </nav>
        <nav className="space-y-0.5 border-t border-slate-200 px-2 py-3">
          {visible(FOOTER_NAV, modules).map((item) => <NavItem key={item.to} item={item} onNavigate={close} />)}
        </nav>
      </aside>

      {open && <div className="fixed inset-0 z-20 bg-slate-900/40 lg:hidden" onClick={close} />}

      <div className="min-w-0 pt-14 lg:ml-[232px] print:ml-0 print:pt-0">
        <main className="p-4 sm:p-6 print:p-0">
          <AnnouncementBanner />
          <div className="mt-4 first:mt-0">{children}</div>
        </main>
      </div>
    </div>
  );
}

export const AppShell = ({ children }) => (
  <TallyProvider>
    <Shell>{children}</Shell>
  </TallyProvider>
);

export default AppShell;
